<template>
    <div class="relative max-w-4xl mx-auto pb-20">

        <!-- Main Workspace card -->
        <div class="relative bg-white rounded-[1rem] shadow-[0_30px_80px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden transition-all duration-500">
            <!-- Grid Background Overlay -->
            <div class="absolute inset-0 opacity-[0.03] pointer-events-none" style="background-image: radial-gradient(#f43f5e 0.5px, transparent 0.5px); background-size: 24px 24px;"></div>
            
            <div class="relative p-6 sm:p-6">
                <!-- Primary Interface -->
                <div class="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    <div v-if="!selectedFile">
                        <!-- URL Input Section (Premium Design) -->
                        <div class="relative group/url mb-1">
                            <div class="flex items-center gap-1 p-0.5 bg-white border-2 border-gray-100 ring-1 ring-logo-blue/40 rounded-[12px] shadow-sm focus-within:border-2 focus-within:border-logo-blue/60 focus-within:ring-4 focus-within:ring-logo-blue/10 transition-all duration-500">
                                <div class="pl-3">
                                    <div>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-youtube size-5 shrink-0 text-primary opacity-80" aria-hidden="true"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"></path><path d="m10 15 5-3-5-3z"></path></svg>
                                    </div>
                                </div>
                                <input 
                                    v-model="videoUrl"
                                    type="text" 
                                    placeholder="Paste YouTube, Reels, or Video URL here..." 
                                    class="flex-1 bg-transparent border-none focus:ring-0 text-gray-900 placeholder:text-gray-400 py-2.5 text-base font-medium"
                                    @keyup.enter="handleUrlSubmit"
                                    @paste="handleUrlPaste"
                                >
                                <button 
                                    @click="handleUrlSubmit"
                                    :disabled="!videoUrl || isProcessing"
                                    class="relative overflow-hidden bg-logo-blue hover:bg-[#6B0B2D] text-white px-6 py-2.5 mr-0.5 rounded-[12px] font-semibold text-sm shadow-lg shadow-logo-blue/20 hover:shadow-logo-blue/30 transition-all duration-300 disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed whitespace-nowrap group/btn"
                                >
                                    <span class="relative z-10">Analyze Video</span>
                                    <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover/btn:animate-[shimmer_1.5s_infinite]"></div>
                                </button>
                            </div>
                        </div>

                        <!-- OR Separator with Premium Lines -->
                        <div class="relative py-2 mb-2">
                            <div class="absolute inset-0 flex items-center" aria-hidden="true">
                                <div class="w-full border-t border-gray-100"></div>
                            </div>
                            <div class="relative flex justify-center">
                                <span class="bg-white px-3 text-[13px] font-semibold text-gray-400 uppercase tracking-[0.3em] border border-gray-50 rounded-full py-1">OR</span>
                            </div>
                        </div>

                        <!-- Dynamic Upload Zone (Existing) -->
                        <div 
                            @click="$refs.fileInput.click()"
                            @dragover.prevent="dragActive = true"
                            @dragleave.prevent="dragActive = false"
                            @drop.prevent="handleDrop"
                            :class="dragActive ? 'border-logo-blue bg-logo-pale scale-[0.99] ring-8 ring-logo-blue/10' : 'border-gray-100 bg-gray-50/50 hover:border-logo-blue/40 hover:bg-white'"
                            class="relative border-2 border-dashed rounded-[1rem] py-12 text-center cursor-pointer transition-all duration-500 group overflow-hidden">
                            
                            <input type="file" ref="fileInput" class="hidden" accept="audio/*,video/*" @change="handleFileSelect">
                            
                            <div class="flex flex-col items-center">
                                <!-- Upload Icon Box -->
                                <div class="w-14 h-14 bg-white rounded-xl shadow-sm border border-gray-200 flex items-center justify-center mb-6 shadow-2xl group-hover:border-logo-blue/30 group-hover:shadow-2xl group-hover:shadow-logo-blue/10 transition-all duration-500">
                                    <Upload class="w-6 h-6 text-black group-hover:text-logo-blue transition-colors duration-500" />
                                </div>
                                <h3 class="text-lg font-semibold text-gray-900 mb-3 group-hover:text-logo-blue transition-colors duration-500">Drag &amp; Drop or Click to upload video here</h3>
                                <div class="flex items-center gap-2 text-gray-500 text-xs font-medium tracking-wide">
                                    <span>Max 25MB</span>
                                    <div class="w-1.5 h-1.5 bg-gray-300 mt-0.5 rounded-full"></div>
                                    <span>MP4 / MPEG / MOV / AVI / WEBM / MP3 / WAV</span>
                                </div>
                            </div>
                        </div>

                        <!-- Trust Badges (Matching Image) -->
                        <div class="flex flex-wrap justify-center items-center gap-x-12 gap-y-6 pt-6">
                            <div class="flex items-center gap-3">
                                <CheckCircle class="w-5 h-5 text-logo-blue" />
                                <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-500">No account required</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <CheckCircle class="w-5 h-5 text-logo-blue" />
                                <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-500">No credit card</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <CheckCircle class="w-5 h-5 text-logo-blue" />
                                <span class="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Free to use</span>
                            </div>
                        </div>
                    </div>

                    <!-- File Active / Processing State -->
                    <div v-else class="space-y-4">
                        <div class="relative overflow-hidden p-4 bg-gradient-to-r from-logo-blue to-logo-dark rounded-[1rem] text-white shadow-lg shadow-logo-blue/20">
                            <div class="absolute -top-6 right-12 p-8 opacity-10 -rotate-12">
                                <img v-if="selectedFile.thumbnail" :src="selectedFile.thumbnail" class="w-24 h-24 object-cover rounded-xl" />
                                <Video v-else-if="selectedFile.type && selectedFile.type.startsWith('video/')" class="w-20 h-20" />
                                <Music v-else class="w-20 h-20" />
                            </div>
                            <div class="relative z-10 flex items-center justify-between">
                                <div class="flex items-center gap-4">
                                    <div class="p-3 bg-white/20 backdrop-blur-md rounded-2xl w-14 h-14 overflow-hidden flex items-center justify-center shrink-0">
                                        <img v-if="selectedFile.thumbnail" :src="selectedFile.thumbnail" class="w-full h-full object-cover rounded-lg" />
                                        <Video v-else-if="selectedFile.type && selectedFile.type.startsWith('video/')" class="w-6 h-6 text-white" />
                                        <Music v-else class="w-6 h-6 text-white" />
                                    </div>
                                    <div class="min-w-0">
                                        <p class="text-lg font-bold truncate max-w-[200px] sm:max-w-md">{{ selectedFile.name }}</p>
                                        <p v-if="selectedFile.type === 'video/url'" class="text-xs text-white/60 font-medium">YouTube Video</p>
                                        <p v-else class="text-xs text-white/60 font-medium">{{ (selectedFile.size / (1024 * 1024)).toFixed(2) }} MB</p>
                                    </div>
                                </div>
                                <button 
                                    @click="resetTranscription" 
                                    :disabled="isProcessing"
                                    :class="isProcessing ? 'opacity-30 cursor-not-allowed pointer-events-none' : 'hover:bg-white/10 text-white/70 hover:text-white'"
                                    class="p-3 rounded-full transition-colors shrink-0"
                                    title="Remove file"
                                >
                                    <X class="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <!-- Advanced AI Loader with Real-time Progress -->
                        <div v-if="isProcessing" class="space-y-8 py-4 animate-in zoom-in-95 duration-500">
                            <div class="relative p-10 bg-gray-50 rounded-[1.5rem] border border-gray-100 overflow-hidden text-center">
                                <div class="flex justify-center gap-2 mb-6">
                                    <div v-for="i in 3" :key="i" class="w-2 h-2 bg-logo-blue rounded-full animate-bounce" :style="`animation-delay: ${i * 0.2}s`"></div>
                                </div>
                                
                                <h4 class="text-xl font-semibold text-gray-800 mb-2">Analyzing Audio Patterns</h4>
                                <p class="text-gray-500 text-sm font-medium mb-8">AI is decoupling voice layers from background noise...</p>
                                
                                <div class="max-w-xs mx-auto space-y-3">
                                    <div class="h-2 bg-gray-200 rounded-full overflow-hidden border border-gray-200/50">
                                        <div 
                                            class="h-full bg-logo-blue rounded-full transition-all duration-500 ease-out"
                                            :style="{ width: progress + '%' }"
                                        ></div>
                                    </div>
                                    <div class="flex justify-between text-[10px] font-bold text-logo-blue uppercase tracking-[0.2em]">
                                        <span>Transcribing</span>
                                        <span>{{ Math.round(progress) }}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Result Section -->
                        <div v-else-if="uploadTranscript" class="animate-in slide-in-from-bottom-8 duration-700">
                            <div class="bg-white rounded-[1rem] border-2 border-gray-100 p-5 shadow-2xl shadow-black/5 relative overflow-hidden">
                                <div class="flex items-center gap-3 mb-1">
                                    <div class="px-3 py-1 bg-logo-blue/10 text-logo-blue text-[10px] font-black uppercase tracking-widest rounded-full border border-logo-blue/10">
                                        Final Transcription
                                    </div>
                                    <div v-if="uploadDetectedLanguage" class="px-3 py-1 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-full border border-emerald-100">
                                        Detected: {{ uploadDetectedLanguage }}
                                    </div>
                                </div>
                                <div class="text-gray-800 text-lg leading-[1.8] font-medium p-2 selection:bg-logo-blue/20 max-h-[300px] overflow-y-auto pr-4">
                                    {{ uploadTranscript }}
                                </div>
                            </div>
                        </div>

                        <!-- Action Bar -->
                        <div v-else class="flex justify-center">
                            <button 
                                @click="handleTranscribeClick" 
                                class="relative group mt-5 mb-5 bg-logo-blue hover:bg-[#6B0B2D] text-white px-6 py-3 rounded-[1rem] font-semibold text-lg shadow-[0_15px_40px_rgba(42,157,143,0.3)] hover:shadow-[0_20px_50px_rgba(42,157,143,0.4)] hover:-translate-y-1 transition-all duration-500 flex items-center gap-4 overflow-hidden active:scale-95"
                            >
                                <div class="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 transition-transform duration-1000"></div>
                                <Zap class="w-4 h-4 fill-current text-white" />
                                Transcribe Now
                            </button>
                        </div>
                        
                        <!-- Controls -->
                        <div v-if="uploadTranscript" class="max-w-2xl mx-auto w-full animate-in slide-in-from-bottom-4 delay-300">
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                <button @click="downloadAudio" class="flex items-center justify-center gap-2 max-w-[190px] py-3 bg-logo-blue text-white hover:bg-[#6B0B2D] border border-gray-100 rounded-2xl hover:shadow-lg transition-all font-bold text-sm group">
                                    <Download class="w-5 h-5" />
                                    Export Audio
                                </button>

                                <button 
                                    @click="copyToClipboard" 
                                    class="flex items-center justify-center gap-2 max-w-[190px] py-3 bg-gray-900 border border-gray-800 rounded-2xl hover:shadow-2xl transition-all font-bold text-sm text-white"
                                    :class="copied ? 'bg-emerald-500 border-emerald-500' : ''"
                                >
                                    <Check v-if="copied" class="w-5 h-5" />
                                    <Copy v-else class="w-5 h-5" />
                                    {{ copied ? 'Copied!' : 'Copy Result' }}
                                </button>
                                
                                <button @click="resetTranscription" class="flex items-center justify-center gap-2 max-w-[190px] py-3 bg-red-50 border border-red-50 rounded-2xl hover:bg-red-500 hover:text-white transition-all text-red-500 font-bold text-sm group">
                                    <RefreshCw class="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                                    Try New
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Error Messages -->
                <div v-if="error" class="mt-5 animate-in slide-in-from-top-4">
                    <div class="bg-red-50 text-red-600 p-6 rounded-[2rem] flex items-center gap-4 text-sm font-black border border-red-100 shadow-sm">
                        <AlertCircle class="w-6 h-6" />
                        {{ error }}
                        <button @click="error = null" class="ml-auto p-2 hover:bg-red-100 rounded-xl transition-colors">
                            <X class="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref } from 'vue';
import axios from 'axios';
import { 
    Copy, Music, X, Video, Upload, Plus,
    AlertCircle, Zap, Check, Download, RefreshCw, CheckCircle
} from 'lucide-vue-next';

export default {
    name: 'VideoToTextComponent',
    components: {
        Copy, Music, X, Video, Upload, Plus, AlertCircle, Zap, Check, Download, RefreshCw, CheckCircle
    },
    setup() {
        // --- States ---
        const uploadTranscript = ref('');
        const uploadAudioUrl = ref(null);
        const uploadDetectedLanguage = ref('');
        const selectedFile = ref(null);
        const videoUrl = ref('');

        const dragActive = ref(false);
        const isProcessing = ref(false);
        const progress = ref(0);
        const copied = ref(false);
        const error = ref(null);

        // --- File Upload Logic ---
        const handleFileSelect = (e) => {
            const file = e.target.files[0];
            if (file) validateAndSetFile(file);
        };

        const handleDrop = (e) => {
            dragActive.value = false;
            const file = e.dataTransfer.files[0];
            if (file) validateAndSetFile(file);
        };

        const setUrlFile = (url) => {
            if (!url) return;
            const cleanUrl = url.trim();
            if (!cleanUrl) return;
            videoUrl.value = cleanUrl;
            selectedFile.value = {
                name: cleanUrl.length > 50 ? cleanUrl.substring(0, 47) + '...' : cleanUrl,
                size: 0,
                type: 'video/url'
            };
            uploadTranscript.value = '';
            uploadDetectedLanguage.value = '';
            error.value = null;
        };

        const handleUrlPaste = (e) => {
            const pastedText = e.clipboardData?.getData('text');
            if (pastedText && (pastedText.startsWith('http://') || pastedText.startsWith('https://') || pastedText.includes('youtu'))) {
                setTimeout(() => {
                    setUrlFile(videoUrl.value || pastedText);
                }, 50);
            }
        };

        const handleUrlSubmit = () => {
            if (videoUrl.value) {
                setUrlFile(videoUrl.value);
            }
        };

        const validateAndSetFile = (file) => {
            const allowedExtensions = ['mp3', 'wav', 'm4a', 'ogg', 'oga', 'webm', 'flac', 'aac', 'mp4', 'mov', 'avi', 'mkv'];
            const fileExtension = file.name.split('.').pop().toLowerCase();

            if (!file.type.startsWith('audio/') && !file.type.startsWith('video/') && !allowedExtensions.includes(fileExtension)) {
                error.value = "Format not supported. Please use common audio/video files.";
                return;
            }
            if (file.size > 25 * 1024 * 1024) {
                error.value = "File exceeds 25MB limit.";
                return;
            }
            if (uploadAudioUrl.value) URL.revokeObjectURL(uploadAudioUrl.value);
            selectedFile.value = file;
            uploadAudioUrl.value = URL.createObjectURL(file);
            uploadTranscript.value = '';
            uploadDetectedLanguage.value = '';
            error.value = null;
        };

        const handleTranscribeClick = () => {
            if (!selectedFile.value) return;
            if (selectedFile.value.type === 'video/url') {
                transcribeUrl();
            } else {
                transcribeFile();
            }
        };

        const transcribeFile = async () => {
            if (!selectedFile.value) return;
            
            isProcessing.value = true;
            progress.value = 0;
            error.value = null;

            // Simulated real-time progress
            const interval = setInterval(() => {
                if (progress.value < 96) {
                    progress.value += Math.random() * 8;
                }
            }, 600);

            const formData = new FormData();
            formData.append('audio', selectedFile.value);

            try {
                const response = await axios.post('/transcribe-audio', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                progress.value = 100;
                
                // Small delay to show 100% completion before results
                setTimeout(() => {
                    uploadTranscript.value = response.data.text;
                    uploadDetectedLanguage.value = response.data.language;
                    isProcessing.value = false;
                    clearInterval(interval);
                }, 800);
            } catch (err) {
                clearInterval(interval);
                isProcessing.value = false;
                error.value = err.response?.data?.error || "Failed to process audio. Please try again.";
            }
        };

        const transcribeUrl = async () => {
            if (!videoUrl.value) return;
            
            isProcessing.value = true;
            progress.value = 0;
            error.value = null;

            // Simulated real-time progress
            const interval = setInterval(() => {
                if (progress.value < 96) {
                    progress.value += Math.random() * 5;
                }
            }, 800);

            try {
                const response = await axios.post('/transcribe-url', {
                    url: videoUrl.value
                });
                progress.value = 100;
                
                setTimeout(() => {
                    uploadTranscript.value = response.data.text;
                    uploadDetectedLanguage.value = response.data.language;
                    
                    // Create a placeholder selected file for the UI state
                    selectedFile.value = {
                        name: response.data.title || videoUrl.value.split('/').pop() || 'Remote Video',
                        size: 0,
                        type: 'video/url',
                        thumbnail: response.data.thumbnail || null
                    };

                    isProcessing.value = false;
                    clearInterval(interval);
                }, 800);
            } catch (err) {
                clearInterval(interval);
                isProcessing.value = false;
                error.value = err.response?.data?.error || "Failed to analyze video URL. Please check the link and try again.";
            }
        };
        
        const resetTranscription = () => {
            if (isProcessing.value) return;
            if (uploadAudioUrl.value) URL.revokeObjectURL(uploadAudioUrl.value);
            selectedFile.value = null;
            uploadAudioUrl.value = null;
            uploadTranscript.value = '';
            uploadDetectedLanguage.value = '';
            videoUrl.value = '';
            error.value = null;
        };

        const copyToClipboard = async () => {
            await navigator.clipboard.writeText(uploadTranscript.value);
            copied.value = true;
            setTimeout(() => copied.value = false, 2000);
        };

        const downloadAudio = () => {
            const url = uploadAudioUrl.value;
            if (!url) return;
            const link = document.createElement('a');
            link.href = url;
            link.download = `recording_${Date.now()}.wav`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        return {
            selectedFile, videoUrl, uploadAudioUrl, dragActive, isProcessing, progress,
            uploadTranscript, uploadDetectedLanguage, error, copied,
            handleFileSelect, handleDrop, handleUrlSubmit, handleUrlPaste, handleTranscribeClick,
            transcribeFile, transcribeUrl, resetTranscription,
            downloadAudio, copyToClipboard
        };
    }
};
</script>

<style scoped>
@keyframes shimmer {
    0% { transform: translateX(-150%); }
    100% { transform: translateX(150%); }
}

/* Glassmorphism utility */
.backdrop-blur-xl {
    backdrop-filter: blur(24px);
}

/* Custom Scrollbar */
::-webkit-scrollbar {
    width: 6px;
}
::-webkit-scrollbar-track {
    background: transparent;
}
::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1;
}
</style>
